import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Col, Spinner } from 'react-bootstrap';
import LinkIcon from '@mui/icons-material/Link';
import S3MediaLink from '../Generic/S3MediaLink';
import DeleteButton from '../Generic/DeleteButton';
import {
  deleteQuote,
  getQuoteDetails,
  getQuoteDetailsLazy,
  invalidateQuoteDetailsCache,
} from '../../clients/quote_client';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import QuoteModal from './QuoteModal';
import OrderDetailModal from '../Order/OrderDetailModal';
import ProductDetailModal from '../Product/ProductDetailModal';
import Table from 'react-bootstrap/Table';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import SupplierCreditDetailModal from '../SupplierCredit/SupplierCreditDetailModal';
import {
  calculatePriceAfterDiscount,
  formatDateStr,
  formatDecimalNumber,
  getCurrencySymbol,
  resolveEntityId,
} from '../../config_and_helpers/helpers';
import { CURRENCY_SYMBOLS } from '../../config_and_helpers/config';
import { formatQuoteBudgetDisplay } from '../../utils/quoteFundingDisplay';

/**
 * Represents a modal for displaying details of a quote.
 * @param {Object} fetchedQuotesRef - The reference to the fetched quotes.
 * @param {function} setFetchedQuotes - The function to update the fetched quotes state.
 * @param {Object} quoteObj - The quote object.
 * @param {function} updateQuotes - The function to update the quotes state.
 * @param {string} quoteId - The quote ID.
 * @param {function} clearSearchValue - The function to clear the search value.
 * @param {function} [onProjectUpdate] - Optional callback to refresh project data after quote edit.
 * @param {boolean} [hideTrigger=false] - Hide the link button (use with controlled `show`).
 * @param {boolean} [show] - Controlled modal visibility.
 * @param {function} [onHide] - Called when controlled modal closes.
 * @returns {JSX.Element} The modal component.
 */
const QuoteDetailModal = ({
  fetchedQuotesRef,
  setFetchedQuotes,
  quoteObj,
  updateQuotes,
  quoteId,
  clearSearchValue,
  onProjectUpdate,
  hideTrigger = false,
  show: controlledShow,
  onHide: controlledOnHide,
}) => {
  // useContext to access global state, useRef for mutable flag, useState for local state management
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const navigate = useNavigate();
  const isLoadingRef = useRef(false);
  const [internalShow, setInternalShow] = useState(false);
  const isControlled = controlledShow !== undefined;
  const show = isControlled ? controlledShow : internalShow;
  const [quote, setQuote] = useState(quoteObj);
  const quoteIdToUse = quoteObj ? quoteObj.id : quoteId;

  // Lazy loading states
  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);

  // UseEffect to update the state on re-renders
  useEffect(() => {
    setQuote(quoteObj);
  }, [quoteObj]);

  // Boolean to determine if the quoteId already exists in the fetched quotes Ref
  const isQuoteInFetchedQuotes = fetchedQuotesRef
    ? fetchedQuotesRef.current.has(quoteId)
    : false;

  // Function to fetch detailed quote data with lazy loading
  const fetchDetailedData = async ({ force = false } = {}) => {
    if (!quoteIdToUse) {
      return;
    }
    if (!force && (detailedData || loadingDetails)) {
      return;
    }

    if (force) {
      invalidateQuoteDetailsCache(quoteIdToUse);
      setDetailedData(null);
    }

    setLoadingDetails(true);
    setError(null);

    try {
      const data = await getQuoteDetailsLazy(token, quoteIdToUse);
      setDetailedData(data);
    } catch (err) {
      setError(err.message || 'Failed to load quote details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleQuoteUpdated = () => {
    fetchDetailedData({ force: true });
    if (updateQuotes) {
      updateQuotes();
    }
  };

  const fetchQuote = () => {
    if (isQuoteInFetchedQuotes) {
      return; // If already fetched this quote, do nothing
    }

    // If rendered via a table (thus passing in the ref), add the quote id to the fetched quotes ref
    if (fetchedQuotesRef) fetchedQuotesRef.current.add(quoteId);

    isLoadingRef.current = true;
    getQuoteDetails(token, quoteIdToUse, setQuote).then(response => {
      if (response.success && setFetchedQuotes) {
        // If fetched, and rendered via a table (thus passing in the setFetchedQuotes function), update the fetched
        // quote state
        setFetchedQuotes(prevFetchedQuotes => [
          ...prevFetchedQuotes,
          response.quoteData,
        ]);
      }
    });
    isLoadingRef.current = false; // Setting loading state to false after API call
  };

  // Remove automatic fetching - will only fetch on hover

  // Function to close the modal.
  const handleClose = () => {
    if (isControlled) {
      controlledOnHide?.();
    } else {
      setInternalShow(false);
    }
    setDetailedData(null);
    setError(null);
  };

  // Function to open the modal.
  const handleShow = () => {
    if (!isControlled) {
      setInternalShow(true);
    }
    if (!quote && quoteIdToUse) {
      fetchQuote();
    }
    fetchDetailedData();
  };

  // Controlled open (e.g. AI chat citation table): fetch immediately.
  useEffect(() => {
    if (!hideTrigger || !show || !quoteIdToUse) return;
    if (!quote) {
      fetchQuote();
    }
    fetchDetailedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideTrigger, show, quoteIdToUse]);

  // Note: Removed early spinner return to allow modal to show with loading state inside

  return (
    <div>
      {/* Always render the button, even when quote data is not yet loaded */}
      {!hideTrigger && (
        <Button
          variant="link"
          onClick={handleShow}
          onMouseEnter={() => {
            // Fetch quote details on hover if not already fetched
            if (!quote && quoteId && !isQuoteInFetchedQuotes) {
              fetchQuote();
            }
          }}
          style={{
            padding: 0,
            border: 'none',
            background: 'none',
            color: '#0d6efd',
            textDecoration: 'underline',
          }}
        >
          {`Quote ${quote ? quote.id : quoteId}`}
        </Button>
      )}

      {hideTrigger && show && !quote && (
        <Modal show onHide={handleClose} centered>
          <Modal.Body className="d-flex align-items-center justify-content-center gap-2 py-4">
            <Spinner animation="border" size="sm" role="status" aria-label="Loading quote" />
            <span>Loading quote…</span>
          </Modal.Body>
        </Modal>
      )}

      {/* Only render the modal if quote data is available */}
      {quote && (
        <Modal
          show={show}
          onHide={handleClose}
          aria-labelledby="quote-detail-modal"
          size="lg"
        >
          <Modal.Header closeButton>
            {/* Modal title displaying the quote ID */}
            <Modal.Title>Quote {quote.id}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {/* Loading state for quote data */}
            {isLoadingRef.current && (
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading quote...</span>
                </Spinner>
                <p className="mt-2">Loading quote data...</p>
              </div>
            )}

            {quote && !isLoadingRef.current && (
              <Container>
                {/* Loading state for detailed data */}
                {loadingDetails && (
                  <div className="text-center mb-3">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-2">Loading quote details...</p>
                  </div>
                )}

                {/* Error state */}
                {error && (
                  <div className="alert alert-danger mb-3">
                    <p className="mb-2">Error loading quote details: {error}</p>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => fetchDetailedData({ force: true })}
                    >
                      Retry
                    </Button>
                  </div>
                )}

                {/* Fallback message when no detailed data */}
                {!loadingDetails && !error && !detailedData && (
                  <div className="alert alert-info mb-3">
                    <p className="mb-0">Loading quote details...</p>
                  </div>
                )}

                {/* Display quote details using detailed data when available */}
                {detailedData && (
                  <>
                    {/* Displaying various details of the quote in a structured format */}
                    <Row>
                      <Col>
                        {/* Label for request date */}
                        <p className="fs-6 fw-bold">Request Date:</p>
                      </Col>
                      <Col>
                        {/* Value for request date */}
                        <p className="fs-6">
                          {detailedData.request_date
                            ? formatDateStr(detailedData.request_date)
                            : ''}
                        </p>
                      </Col>
                    </Row>
                    {/* Additional rows follow the same structure for different quote details */}
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Creation/Reception Date:</p>
                      </Col>
                      <Col>
                        <p className="fs-6">
                          {detailedData.creation_date
                            ? formatDateStr(detailedData.creation_date)
                            : ''}
                        </p>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Last Updated:</p>
                      </Col>
                      <Col>
                        <p className="fs-6">
                          {detailedData.last_updated
                            ? formatDateStr(detailedData.last_updated)
                            : ''}
                        </p>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">URL: </p>
                      </Col>
                      <Col>
                        <S3MediaLink
                          url={detailedData.quote_url}
                          quoteId={detailedData.id}
                          title="Open quote PDF"
                        >
                          <LinkIcon
                            sx={{ fontSize: '36px', alignSelf: 'middle' }}
                          />
                        </S3MediaLink>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Order: </p>
                      </Col>
                      <Col>
                        <OrderDetailModal
                          orderId={resolveEntityId(detailedData?.order)}
                          updateOrders={handleQuoteUpdated}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Supplier: </p>
                      </Col>
                      <Col>
                        <SupplierDetailModal
                          supplierObj={detailedData.supplier}
                          supplierId={detailedData.supplier.id}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Status: </p>
                      </Col>
                      <Col>
                        <p className="fs-6">{detailedData.status}</p>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Demand Reference: </p>
                      </Col>
                      <Col>
                        <p className="fs-6">
                          {detailedData.corporate_demand_ref}
                        </p>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Funding: </p>
                      </Col>
                      <Col>
                        {(() => {
                          const fundingCredit =
                            detailedData.funding_credit ??
                            detailedData.supplier_credit ??
                            null;
                          const fundingCreditId =
                            (typeof fundingCredit === 'object' &&
                              fundingCredit?.id) ||
                            (fundingCredit != null && fundingCredit) ||
                            detailedData.funding_credit_id ||
                            detailedData.supplier_credit_id ||
                            null;

                          if (fundingCreditId) {
                            return (
                              <span className="fs-6">
                                <SupplierCreditDetailModal
                                  creditObj={
                                    typeof fundingCredit === 'object'
                                      ? fundingCredit
                                      : undefined
                                  }
                                  creditId={fundingCreditId}
                                />
                              </span>
                            );
                          }

                          return (
                            <p className="fs-6">
                              {formatQuoteBudgetDisplay(detailedData)}
                            </p>
                          );
                        })()}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Project:</p>
                      </Col>
                      <Col>
                        {detailedData.project?.id ? (
                          <Button
                            variant="link"
                            className="p-0 fs-6 align-baseline"
                            onClick={() => {
                              handleClose();
                              navigate(`/projects/${detailedData.project.id}`);
                            }}
                          >
                            {detailedData.project.name || `Project ${detailedData.project.id}`}
                          </Button>
                        ) : (
                          <p className="fs-6 text-muted mb-0">No project</p>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Associated User: </p>
                      </Col>
                      <Col>
                        {detailedData.user ? (
                          <p className="fs-6">
                            {detailedData.user.first_name}{' '}
                            {detailedData.user.last_name}
                          </p>
                        ) : (
                          <p className="fs-6 text-muted">No user associated</p>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Quote Items: </p>
                      </Col>
                    </Row>

                    {/* Displaying quote items in a table format */}
                    <Table striped bordered hover>
                      <thead>
                        <tr className="text-center">
                          <th>#</th>
                          <th>Product</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th>Discount</th>
                          <th>Post-Discount Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Mapping over each item in the quote to create a table row */}
                        {detailedData?.items?.map((item, index) => (
                          <React.Fragment key={index}>
                            <tr className="text-center italic-text">
                              <td>{index + 1}</td>
                              <td>
                                {/* Displaying product details in a modal */}
                                <ProductDetailModal
                                  productObj={item.product}
                                  productId={item.product.id}
                                />
                              </td>
                              <td>{item.quantity}</td>
                              <td>
                                {/*  Display the price formatted as a decimal */}
                                {formatDecimalNumber(item.price)}
                                {/* If the item's currency has a symbol, append it to the price */}
                                {CURRENCY_SYMBOLS[item.currency]
                                  ? `${getCurrencySymbol(item.currency)}`
                                  : ''}
                              </td>
                              <td>
                                {/* If item has a discount, display it formatted as decimal followed by a percentage sign */}
                                {item?.discount !== null
                                  ? `${formatDecimalNumber(item?.discount)}%`
                                  : null}
                              </td>
                              <td>
                                {/* If the item has a discount, calculate the price after discount, else display null */}
                                {item?.discount !== null
                                  ? calculatePriceAfterDiscount(
                                      item.price,
                                      item?.discount
                                    )
                                  : null}
                              </td>
                            </tr>
                          </React.Fragment>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </Container>
            )}
          </Modal.Body>

          {quote && (
            <Modal.Footer className="d-flex flex-row justify-content-between">
              <div className="d-flex flex-row">
                {/* Edit button for the quote - only show when detailed data is available */}
                {detailedData && !isViewer && (
                  <div className="me-2">
                    <QuoteModal
                      quoteObj={detailedData}
                      clearSearchValue={clearSearchValue}
                      disableEdit={!!detailedData.order}
                      onSuccessfulSubmit={handleQuoteUpdated}
                      projectId={resolveEntityId(detailedData.project)}
                      onProjectUpdate={onProjectUpdate}
                    />
                  </div>
                )}
                {/* Delete button for the quote - only show when detailed data is available */}
                {detailedData && (
                  <DeleteButton
                    // Disable deletion if quote is related to an order
                    disableDelete={!!detailedData.order}
                    objectType="quote"
                    objectName={detailedData.id}
                    objectId={detailedData.id}
                    deleteFetchFunc={deleteQuote}
                    onSuccessfulDelete={updateQuotes}
                    clearSearchValue={clearSearchValue}
                  />
                )}
              </div>
              {/* Close button for the modal */}
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
            </Modal.Footer>
          )}
        </Modal>
      )}
    </div>
  );
};
export default QuoteDetailModal;
